// @flow
import {BaseUtils} from "./base-utils";

/**
 * BaseRetryUtils - Abstract base class for retry logic with exponential backoff
 *
 * Provides generic retry mechanisms that can be extended by specific analytics providers.
 * Each provider should extend this class and define their own retryable/non-retryable error codes.
 */
export class BaseRetryUtils extends BaseUtils {
  static injectables = [...BaseUtils.injectables];

  // Default exponential backoff delays in milliseconds for each retry attempt
  // SHOULD be overridden by subclasses to match provider-specific rate limiting behavior
  // Default: 3 retries with moderate delays
  static BACKOFF_DELAYS = [1000, 5000, 15000]; // 1s, 5s, 15s (default)

  // Provider-specific error codes that should trigger a retry
  // MUST be overridden by subclasses with provider-specific codes
  static RETRYABLE_ERROR_CODES = [];

  // Network-related error names that should trigger a retry (common across all providers)
  static RETRYABLE_ERROR_NAMES = [
    'TimeoutError',
    'ConnectTimeoutError',
    'NetworkingError',
    'ECONNRESET',
    'ENOTFOUND',
    'ETIMEDOUT',
    'ECONNREFUSED'
  ];

  // Provider-specific non-retryable error codes - permanent failures
  // Should be overridden by subclasses with provider-specific codes
  static NON_RETRYABLE_ERROR_CODES = [];

  /**
   * Check if an error is retryable based on provider-specific error codes or network errors
   *
   * @param {Error|String|Object} error - Error object, error code string, or provider-specific error
   * @returns {Boolean} - True if the error should trigger a retry
   */
  isRetryableError(error: any): boolean {
    if (!error) {
      return false;
    }

    const constructor = this.constructor;

    // Handle string error codes directly (for provider APIs that return error codes as strings)
    if (typeof error === 'string') {
      return constructor.RETRYABLE_ERROR_CODES.includes(error);
    }

    // Check if it's explicitly non-retryable first
    const errorCode = error.code || error.Code || error.name || error.__type;
    if (errorCode && constructor.NON_RETRYABLE_ERROR_CODES.includes(errorCode)) {
      return false;
    }

    // Check retryable error codes
    if (errorCode && constructor.RETRYABLE_ERROR_CODES.includes(errorCode)) {
      return true;
    }

    // Check retryable error names (network errors - common to all providers)
    const errorName = error.name || error.code;
    if (errorName && constructor.RETRYABLE_ERROR_NAMES.includes(errorName)) {
      return true;
    }

    // Check for network timeout errors in message
    const errorMessage = error.message || error.Message || '';
    if (errorMessage.toLowerCase().includes('timeout') ||
        errorMessage.toLowerCase().includes('network') ||
        errorMessage.toLowerCase().includes('connect')) {
      return true;
    }

    // Default to not retryable
    return false;
  }

  /**
   * Get the backoff delay for a specific retry attempt
   *
   * @param {Number} attemptNumber - Zero-based retry attempt number (0 = first retry)
   * @returns {Number} - Delay in milliseconds
   */
  getBackoffDelay(attemptNumber: number): number {
    if (attemptNumber < 0) {
      return 0;
    }

    const constructor = this.constructor;
    if (attemptNumber >= constructor.BACKOFF_DELAYS.length) {
      // Return the maximum delay for attempts beyond our array
      return constructor.BACKOFF_DELAYS[constructor.BACKOFF_DELAYS.length - 1];
    }

    return constructor.BACKOFF_DELAYS[attemptNumber];
  }

  /**
   * Sleep for a specified number of milliseconds
   *
   * @param {Number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute a function with automatic retry on retryable errors
   *
   * @param {Function} fn - Async function to execute
   * @param {Object} options - Retry options
   * @param {Number} options.maxRetries - Maximum number of retry attempts (default: based on BACKOFF_DELAYS length)
   * @param {Function} options.shouldRetry - Custom function to determine if error is retryable
   * @param {Function} options.onRetry - Callback executed before each retry attempt
   * @returns {Promise<*>} - Result of the function
   */
  async withRetry(
    fn: () => Promise<any>,
    options: {
      maxRetries?: number,
      shouldRetry?: (error: any) => boolean,
      onRetry?: (error: any, attemptNumber: number, delay: number) => void
    } = {}
  ): Promise<any> {
    const maxRetries = options.maxRetries !== undefined ? options.maxRetries : this.getMaxRetries();
    const shouldRetry = options.shouldRetry || this.isRetryableError.bind(this);
    const onRetry = options.onRetry;

    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;

        // Check if we should retry
        const isLastAttempt = attempt === maxRetries;
        const errorIsRetryable = shouldRetry(error);

        if (isLastAttempt || !errorIsRetryable) {
          throw error;
        }

        // Calculate delay and wait
        const delay = this.getBackoffDelay(attempt);

        // Call onRetry callback if provided
        if (onRetry) {
          onRetry(error, attempt, delay);
        }

        await this.sleep(delay);
      }
    }

    // Should never reach here, but throw last error as fallback
    throw lastError;
  }

  /**
   * Get the maximum number of retry attempts
   *
   * @returns {Number} - Maximum retry attempts
   */
  getMaxRetries(): number {
    return this.constructor.BACKOFF_DELAYS.length;
  }

  /**
   * Get the total maximum backoff time in milliseconds
   *
   * @returns {Number} - Total max backoff time
   */
  getTotalMaxBackoffTime(): number {
    return this.constructor.BACKOFF_DELAYS.reduce((sum, delay) => sum + delay, 0);
  }
}
